<template>
  <div class="ui-weekday-picker">
    <div class="ui-weekday-picker__head">
      <span :id="labelId" class="ui-weekday-picker__label">{{ label }}</span>

      <span class="ui-weekday-picker__summary">{{ summary }}</span>
    </div>

    <div class="ui-weekday-picker__days" role="group" :aria-labelledby="labelId">
      <button
        v-for="weekday in WEEKDAYS"
        :key="weekday.key"
        type="button"
        class="ui-weekday-picker__day"
        :class="{ 'is-selected': isSelected(weekday.key) }"
        :aria-pressed="isSelected(weekday.key)"
        :aria-label="t(weekday.fullName)"
        :disabled="disabled"
        @click="toggle(weekday.key)"
      >
        {{ t(weekday.shortName) }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

type WeekdayKey =
  | "onMonday"
  | "onTuesday"
  | "onWednesday"
  | "onThursday"
  | "onFriday"
  | "onSaturday"
  | "onSunday";

type WeekdaysSelection = Partial<Record<WeekdayKey, boolean>>;

const WEEKDAYS: { key: WeekdayKey; shortName: string; fullName: string }[] = [
  { key: "onMonday", shortName: "Mon", fullName: "Monday" },
  { key: "onTuesday", shortName: "Tue", fullName: "Tuesday" },
  { key: "onWednesday", shortName: "Wed", fullName: "Wednesday" },
  { key: "onThursday", shortName: "Thu", fullName: "Thursday" },
  { key: "onFriday", shortName: "Fri", fullName: "Friday" },
  { key: "onSaturday", shortName: "Sat", fullName: "Saturday" },
  { key: "onSunday", shortName: "Sun", fullName: "Sunday" },
];

const WORKING_DAYS: WeekdayKey[] = [
  "onMonday",
  "onTuesday",
  "onWednesday",
  "onThursday",
  "onFriday",
];

const props = withDefaults(
  defineProps<{
    modelValue: WeekdaysSelection;
    label: string;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: WeekdaysSelection];
}>();

const { t } = useI18n();

const labelId = `ui-weekday-picker-${useId()}`;

const isSelected = (key: WeekdayKey): boolean => props.modelValue[key] === true;

const selectedKeys = computed(() => WEEKDAYS.filter((weekday) => isSelected(weekday.key)));

const summary = computed(() => {
  const count = selectedKeys.value.length;

  if (count === 0) {
    return t("No days selected");
  }

  const isWorkingWeek =
    count === WORKING_DAYS.length &&
    selectedKeys.value.every((weekday) => WORKING_DAYS.includes(weekday.key));

  if (isWorkingWeek) {
    return t("Working days (5)");
  }

  return t("Selected: {count}", { count });
});

const toggle = (key: WeekdayKey): void => {
  emit("update:modelValue", { ...props.modelValue, [key]: !isSelected(key) });
};
</script>
